import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import {
  downloadFile,
  getDriveFileById,
  getFileById,
} from "../../../services/fileServices/FileService";
import { useAuth } from "../../../firebase/AuthProvider";
import DriveFile from "../../../interfaces/file/DriveFile";
import PDFViewer from "./pdfViewer/PDFViewer"; // The same viewer, but we’ll pass canEdit={false}
import FileViewer from "react-file-viewer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatFileSize } from "../../../utilities/fileUtils";
import { formatDateTime } from "../../../utilities/dateUtils";
import {
  faAngleDown,
  faAngleRight,
  faArrowLeft,
  faCalendarDays,
  faCheckCircle,
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faCodeBranch,
  faCopy,
  faDownload,
  faFileSignature,
  faFont,
  faICursor,
  faPenFancy,
  faSignature,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { getSignatureRequestsForFile } from "../../../services/fileServices/signatures/SignatureService";
import { SignatureRequest } from "../../../interfaces/file/fileSignature/SignatureRequest";
import { SignatureType } from "../../../interfaces/file/fileSignature/SignatureBox";
import { mapSignatureBoxToSignatureField } from "../../../utilities/MapFunctions";
import { Button, Form } from "react-bootstrap";
import backarrowbtn from '../../../assets/images/backarrowbtn.svg';
import { toast } from "react-toastify";
import { FileNavigationState } from "../../../interfaces/task/Task";
import DocumentInfo from "./pdfViewer/editorComponents/DocumentInfo";
import { SignatureStatus } from "../../../interfaces/file/fileSignature/SignatureBox";
import moment from "moment";
import ErrorDocProgressLoader from "./pdfViewer/guestSignComponents/ErrorDocProgressLoader";

const FileViewPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { fileId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as FileNavigationState | null;

  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileMetadata, setFileMetadata] = useState<DriveFile | null>(null);
  const [blobMimeType, setBlobMimeType] = useState<string>("");
  const [zoom, setZoom] = useState(1.0); // For images
  const [textContent, setTextContent] = useState<string>("");
  const [showVersionList, setShowVersionList] = useState<boolean>(false);
  const [signatureRequests, setSignatureRequests] = useState<
    SignatureRequest[]
  >([]);
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageInput, setPageInput] = useState<string>(currentPage.toString());
  const [activeNav, setActiveNav] = useState<string | null>("document");
  const [userCanEdit, setUserCanEdit] = useState<boolean>(true);
  const [isVersionHistoryVisible, setVersionHistoryVisible] = useState(false);
  const [signatureStatus, setSignatureStatus] = useState<SignatureStatus | undefined>(undefined);
  const [progress, setProgress] = useState<number>(0);

  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const progressIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  };

  const handlePageInputBlur = () => {
    const pageNumber = parseInt(pageInput, 10);
    if (!isNaN(pageNumber) && numPages && pageNumber >= 1 && pageNumber <= numPages) {
      setCurrentPage(pageNumber);
    } else {
      setPageInput(currentPage.toString());
    }
  };

  const handlePageInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handlePageInputBlur();
      (e.target as HTMLInputElement).blur();
    }
  };

  useEffect(() => {
    // User can edit only if file is the latest version (not finalized)
    setUserCanEdit(!!fileMetadata?.latest);
  }, [fileMetadata, currentUser]);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });
    if (pdfContainerRef.current) {
      observer.observe(pdfContainerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchFile = async () => {
      if (!fileId || !currentUser) return;

      try {
        // Before starting fetch:
        setProgress(0);
        // ensure any prior interval is cleared
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        // start interval to increment by 5% every 100ms up to 90%
        progressIntervalRef.current = window.setInterval(() => {
          setProgress(prev => {
            const next = Math.min(90, prev + 5);
            if (next >= 90 && progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            return next;
          });
        }, 100);

        const authToken = await currentUser.getIdToken();
        const activeSignatureFields = await getSignatureRequestsForFile(
          authToken,
          fileId
        );
        if (activeSignatureFields) setSignatureRequests(activeSignatureFields);
        const file = await getDriveFileById(fileId, authToken);
        if (file) {
          setFileMetadata(file);
          const url = await getFileById(fileId, authToken);
          if (url) {
            setFileUrl(url);
            const response = await fetch(url);
            const blob = await response.blob();
            let inferredMimeType = file.type.type || blob.type;
            setBlobMimeType(inferredMimeType);
            if (inferredMimeType === "text/plain") {
              const text = await response.text();
              setTextContent(text);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching file:", error);
        setProgress(100);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      }
    };

    fetchFile();
  }, [fileId, currentUser]);

  useEffect(() => {
    const allBoxes = signatureRequests.flatMap(sr => sr.signatureBoxes || []);
    if (allBoxes.length === 0) {
      setSignatureStatus(undefined);
      return;
    }
    const hasPending = allBoxes.some(box => !(box.signatureValue && box.signatureValue.trim()));
    setSignatureStatus(hasPending ? "pending" : "success");
  }, [signatureRequests]);

  const renderViewer = () => {
    if (!fileUrl || !fileMetadata) return <div><ErrorDocProgressLoader progress={progress} /></div>;

    const extension = (fileMetadata.fileName?.split(".").pop() || "").toLowerCase();

    if (fileMetadata.mimeType == "pdf" || blobMimeType.toLocaleLowerCase() === "pdf" || blobMimeType === "application/pdf" || extension === "pdf") {
      return (
        <>
          {containerWidth > 0 && (
            <PDFViewer
              fileUrl={fileUrl}
              containerWidth={containerWidth}
              signatureFields={signatureRequests.flatMap(req =>
                req.signatureBoxes.map(box => mapSignatureBoxToSignatureField(box))
              )}
              canEdit={false}
              selectedSignerEmail=""
              onSignatureFieldsChange={() => { }}
              currentPage={currentPage}
              setNumPages={setNumPages}
              selectedBoxId={selectedBoxId}
              initialFieldIds={new Set<string>}
            />
          )}
        </>
      );
    }

    if (blobMimeType.startsWith("image/") || ["jpg", "jpeg", "png", "gif"].includes(extension)) {
      return (
        <div>
          <div style={{ transform: `scale(${zoom})`, transformOrigin: "center", display: "inline-block" }}>
            <img src={fileUrl} alt={fileMetadata.fileName} />
          </div>
        </div>
      );
    }

    if (blobMimeType === "text/plain" || ["txt", "csv", "log"].includes(extension)) {
      return (
        <div className="border p-4 rounded bg-white max-w-full overflow-auto">
          <pre className="whitespace-pre-wrap break-words">{textContent}</pre>
        </div>
      );
    }

    if (blobMimeType.startsWith("video/") || ["mp4", "webm", "mov", "avi"].includes(extension)) {
      return <video src={fileUrl} controls style={{ maxWidth: "100%", maxHeight: "70vh" }} />;
    }

    const fileType = (() => {
      if (extension === "doc" || extension === "docx") return "docx";
      if (extension === "xls" || extension === "xlsx") return "xlsx";
      return extension || "unknown";
    })();

    return <FileViewer fileType={fileType} filePath={fileUrl} onError={(err) => console.log("FileViewer error:", err)} />;
  };

  const handleDownload = async () => {
    if (!fileId || !fileMetadata || !currentUser) return;
    try {
      const authToken = await currentUser.getIdToken();
      const blob = await downloadFile(fileId, authToken);
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileMetadata.fileName;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const toggleNav = (elementName: string) => {
    setActiveNav(activeNav === elementName ? null : elementName);
  };

  const toggleVersionHistory = () => {
    setVersionHistoryVisible(!isVersionHistoryVisible);
  };

  const handleSelectBox = (box: any) => {
    setSelectedBoxId(box.id);
    setCurrentPage(box.pageNumber);
    if (pdfContainerRef.current) {
      // This is a rough scroll, PDF viewer's internal scaling might affect it
      pdfContainerRef.current.scrollTo({ top: box.y - 50, behavior: 'smooth' });
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied to clipboard!");
    }, (err) => {
      toast.error("Failed to copy.");
      console.error('Could not copy text: ', err);
    });
  };

  const isPdf = fileMetadata && (blobMimeType === "application/pdf" || (fileMetadata.fileName?.split(".").pop() || "").toLowerCase() === "pdf");

  return (
    <>
      <div className="common_card">
        <div className="top top_bar_flx mb-3">
          <div className="top_bar_tittle flex items-center align-middle">
            <button onClick={() => {
              // Check if coming from SignDocument page
              // If state has fromSignDocument flag, navigate to SignDocument with sent tab (default)
              if (location.state?.fromSignDocument) {
                // Navigate to SignDocument without eventkey param (defaults to sent tab)
                navigate('/signDocument');
              } else {
                navigate(-1);
              }
            }}> <img style={{ width: '1.5rem' }} src={backarrowbtn} alt="backarrowbtn" /> </button>
            <h2 className="m-0">
              {fileMetadata?.fileName}
            </h2>
          </div>
          <div className="top_right_flx">
            <button onClick={handleDownload} className="border px-3 py-1 rounded">
              <FontAwesomeIcon icon={faDownload} />
            </button>
            {userCanEdit && fileMetadata?.latest && signatureRequests.length > 0 && (
              <div className="text-right">
                <Button
                  variant="primary"
                  className="btn_sm"
                  onClick={() => {
                    navigate(`/edit/file/${fileId}`, {
                      state: {
                        taskId: state?.taskId ?? null,
                        taskTitle: state?.taskTitle ?? null,
                      } satisfies FileNavigationState,
                    });
                  }}
                  title="Request a signature for this document."
                >
                  Modify Request
                  <FontAwesomeIcon icon={faPenFancy} className="ml-2" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Updated Warning Styling */}
        {/* {!fileMetadata?.latest && (
          <div 
            className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4 flex items-center" 
            role="alert"
          >
            <FontAwesomeIcon icon={faTriangleExclamation} className="mr-3" />
            <div>
              <p className="font-bold">Warning!</p>
              <p>You are not viewing the latest version of this file.</p>
            </div>
          </div>
        )} */}

        <div className="sign_body">
          <div className="sign_left">
            {isPdf && (
              <div className="flex gap-2 mb-4 justify-start items-center">
                {numPages && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline-secondary"
                      className="btn_sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center">
                      <Form.Control
                        type="number"
                        value={pageInput}
                        onChange={handlePageInputChange}
                        onBlur={handlePageInputBlur}
                        onKeyPress={handlePageInputKeyPress}
                        className="w-16 text-center form-control-sm"
                        min="1"
                        max={numPages}
                      />
                      <span className="text-sm min-w-10 text-gray-500 ml-1 flex flex-col">{"/ " + numPages}</span>
                    </div>
                    <Button
                      variant="outline-secondary"
                      className="btn_sm"
                      onClick={() => numPages && setCurrentPage((p) => Math.min(numPages, p + 1))}
                      disabled={!numPages || currentPage >= numPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
            <div ref={pdfContainerRef} className="pdfViewer" style={{ maxHeight: 'calc(100vh - 250px)' }}>
              {renderViewer()}
            </div>
          </div>
          <div className="sign_right">
            <div className="common_card h-100">
              {/* <div onClick={() => toggleNav("document")} className="top top_bar_flx border_tittle cursor-pointer">
                                <div className="top_bar_tittle">
                                    <h2>Document</h2>
                                </div>
                                <FontAwesomeIcon style={{ width: '1.5rem' }} className="text-inactive cursor-pointer" icon={activeNav === "document" ? faChevronDown : faChevronRight} />
                            </div> */}
              {
                <DocumentInfo
                  fileMetadata={fileMetadata}
                  activeNav={activeNav}
                  toggleNav={toggleNav}
                  status={signatureStatus}
                  taskId={state?.taskId || null}
                  taskTitle={state?.taskTitle}
                />
              }
              <div>
                <div onClick={() => toggleNav("signature-requests")} className="top top_bar_flx border_tittle cursor-pointer">
                  <div className="top_bar_tittle">
                    <h2>Signature Requests</h2>
                  </div>
                  <FontAwesomeIcon className="text-inactive cursor-pointer" icon={activeNav === "signature-requests" ? faChevronDown : faChevronRight} />
                </div>
                {activeNav === "signature-requests" && (
                  <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                    {signatureRequests.map((sr, index) => {
                      return (
                        <div key={index} className="">
                          {sr.signatureBoxes.map((field) => (
                            <div
                              key={field.id}
                              onClick={() => handleSelectBox(field)}
                              className={`p-3 rounded-lg mb-2 cursor-pointer border ${field.id === selectedBoxId
                                ? 'border-purple-500 bg-purple-100'
                                : field.signatureValue
                                  ? 'bg-green-200 border-green-500 hover:bg-green-100'
                                  : 'bg-gray-50 hover:bg-gray-100'
                                }`}
                            >
                              <div className="flex align-middle items-center gap-2">
                                {field.boxType.name === SignatureType.DATE && <FontAwesomeIcon className="w-6" icon={faCalendarDays} />}
                                {field.boxType.name === SignatureType.SIGNATURE && <FontAwesomeIcon className="w-6" icon={faSignature} />}
                                {field.boxType.name === SignatureType.INITIALS && <FontAwesomeIcon className="w-6" icon={faFont} />}
                                {field.boxType.name === SignatureType.TEXT && <FontAwesomeIcon className="w-6" icon={faICursor} />}
                                <div className="text_turcate">{field.assignedSignerEmail || "(none)"}</div>
                              </div>

                              {/* Status row */}
                              <div className="mt-1 text-xs flex items-center gap-1">
                                {field.signatureValue ? (
                                  <>
                                    <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                                    <span className="text-green-600">
                                      Signed{" "}
                                      {moment(field.signedAt).format("MM/DD/YYYY")}

                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
                                    <span className="text-orange-600">
                                      Pending signature
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    })}
                    {signatureRequests.length === 0 && (
                      <div className="stats">
                        <p>There are currently no Signature Fields created.</p>
                      </div>
                    )}
                  </div>
                )}
                {activeNav === "signature-requests" && selectedBoxId && (
                  <div className="mt-4 p-3">
                    <h3 className="font-bold mb-3 text-lg border-b">Signature Details</h3>
                    {(() => {
                      let selectedRequest: SignatureRequest | undefined;
                      let selectedBox: any | undefined;

                      for (const sr of signatureRequests) {
                        const box = sr.signatureBoxes.find(b => b.id === selectedBoxId);
                        if (box) {
                          selectedRequest = sr;
                          selectedBox = box;
                          break;
                        }
                      }

                      if (!selectedRequest || !selectedBox) {
                        return <p>Details not found.</p>;
                      }

                      const isSigned = !!selectedBox.signatureValue?.trim();

                      return (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-gray-600">Signer:</span>
                            <div className="flex items-center">
                              <span className="text-gray-800 truncate" style={{ maxWidth: '150px' }} title={selectedBox.assignedSignerEmail}>{selectedBox.assignedSignerEmail}</span>
                              <button onClick={() => handleCopyToClipboard(selectedBox.assignedSignerEmail)} className="ml-2 text-gray-500 hover:text-gray-700">
                                <FontAwesomeIcon icon={faCopy} />
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-gray-600">Box ID:</span>
                            <div className="flex items-center">
                              <span className="text-gray-800 truncate" style={{ maxWidth: '150px' }} title={selectedBox.id}>{selectedBox.id}</span>
                              <button onClick={() => handleCopyToClipboard(selectedBox.id)} className="ml-2 text-gray-500 hover:text-gray-700">
                                <FontAwesomeIcon icon={faCopy} />
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-semibold text-gray-600">Signature Type:</span>
                            <span className="text-gray-800">{selectedBox.boxType.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-semibold text-gray-600">Requested:</span>
                            <span className="text-gray-800">{formatDateTime(new Date(selectedRequest.createdAt))}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-gray-600">Status:</span>
                            {isSigned ? (
                              <span className="px-2 py-1 text-xs font-bold text-green-800 bg-green-200 rounded-full flex items-center">
                                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                Complete
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-bold text-yellow-800 bg-yellow-200 rounded-full">
                                Pending
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default FileViewPage;